import { useCallback, useState } from 'react';

import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/common/Button';
import CopyButton from '@/components/common/CopyButton';
import { Textarea } from '@/components/shadcn/textarea';
import getEditDistanceRatio from '@/features/compose/utils/getEditDistanceRatio';
import cn from '@/lib/utils/cn';

import ChatBubble from './ChatBubble';
import StreamingText from './StreamingText';

export type ConceptReplyCopyEvent = {
  method: 'copy-button' | 'textarea';
  editedRatio: number;
};

export type ConceptReplyRating = 'positive' | 'negative';

type Props = {
  initialText: string;
  onCopied: (event: ConceptReplyCopyEvent) => void;
  onRated: (rating: ConceptReplyRating) => void;
};

export default function ConceptReplyResult({
  initialText,
  onCopied,
  onRated,
}: Props) {
  const { t } = useTranslation();

  const [text, setText] = useState(initialText);
  const [hasRevealed, setHasRevealed] = useState(false);
  const [rating, setRating] = useState<ConceptReplyRating>();

  const finishReveal = useCallback(() => setHasRevealed(true), []);

  const handleCopied = (method: ConceptReplyCopyEvent['method']) => {
    onCopied({ method, editedRatio: getEditDistanceRatio(initialText, text) });
  };

  const handleRated = (newRating: ConceptReplyRating) => {
    if (newRating === rating) return;

    setRating(newRating);
    onRated(newRating);
  };

  return (
    <div className="flex flex-col gap-2">
      <ChatBubble from="assistant">
        <p>{t('CONCEPT_REPLY_RESULT_INTRO')}</p>

        <p className="mt-1">{t('CONCEPT_REPLY_RESULT_HINT')}</p>
      </ChatBubble>

      {!hasRevealed && (
        <div className="border-input min-h-16 rounded-md border px-3 py-2 text-sm whitespace-pre-wrap shadow-xs">
          <StreamingText text={text} onDone={finishReveal} />
        </div>
      )}

      {hasRevealed && (
        <div className="flex flex-col gap-1.5">
          <Textarea
            className="min-h-0"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onCopy={() => handleCopied('textarea')}
          />
        </div>
      )}

      <div className="flex justify-between">
        <div className="flex gap-0.5">
          <Button
            size="icon-xs"
            variant="ghost"
            onClick={() => handleRated('positive')}
          >
            <ThumbsUp
              className={cn('size-3.5 opacity-70', {
                ['fill-current opacity-100']: rating === 'positive',
              })}
            />
          </Button>

          <Button
            size="icon-xs"
            variant="ghost"
            onClick={() => handleRated('negative')}
          >
            <ThumbsDown
              className={cn('size-3.5 opacity-70', {
                ['fill-current opacity-100']: rating === 'negative',
              })}
            />
          </Button>
        </div>

        <CopyButton
          variant="default"
          size="sm"
          text={text}
          onCopy={() => handleCopied('copy-button')}
        >
          {t('CONCEPT_REPLY_COPY_BUTTON')}
        </CopyButton>
      </div>
    </div>
  );
}
